import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRecords, PageHead, Err, Empty } from '../ui.js';
import {
  startResearch, getResearchJob, startBuild, getBuildJob,
  listSuggestions, markSuggestionAdded, dismissSuggestion,
  revealContact, createRecord, ApiError,
} from '../api.js';
import type {
  IcpProfile, Persona, DiscoveredAccount, ContactSuggestion,
  CompanyCard, AgentJob, BuildJob,
} from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// SOURCING, END TO END, ON ONE SCREEN.
//
// Every part of this already worked and lived on a different page. ICP on /icp,
// finding accounts on /research, enrichment and people on /build, revealing an
// address on /contacts. Four screens, and the ORDER between them existed only
// in Kyle's head — you cannot search for people at an account you have not
// promoted, or score a candidate against an ICP you have not written.
//
// The handoffs were already there (`?slugs=`), which is the tell that this was
// always one job wearing four hats.
//
// ─────────────────────────────────────────────────────────────────────────────
// IT IS A WIZARD, WHICH MEANS ONE STEP IS LIVE AND THE REST ARE HONEST ABOUT WHY.
//
// Same rule as Setup.tsx: the order is a DEPENDENCY CHAIN, not a preference.
// Showing four equal options is how somebody picks the one that cannot work yet
// and concludes the app is broken. So each step states what it needs, and a step
// whose input does not exist says so instead of failing when pressed.
//
// YOU CAN START ANYWHERE THE DATA ALLOWS. Kyle has 28 accounts already; making
// him run the research agent to reach enrichment would be a wizard that serves
// itself. Any step whose precondition is already met is open.
//
// ─────────────────────────────────────────────────────────────────────────────
// EVERY PAID STEP PRICES ITSELF BEFORE IT RUNS, AND NEVER IN THE WARM BUTTON.
//
// Two of the four steps spend: firmographics at a credit per account, and an
// address at a credit per person. Both say the number first, both are skippable,
// and neither is the primary action. The free half — the people search — is the
// one that looks inviting, because it is the one that costs nothing.
//
// IT ADDS NO NEW ENDPOINT. Every action here is the same audited call the
// individual screens make, so there is still exactly one code path that spends
// an Apollo credit and one place it is written down.
// ─────────────────────────────────────────────────────────────────────────────

type Discovered = DiscoveredAccount & { id: string };
type Company = CompanyCard & { id: string; slug: string; name: string };

const LIVE = ['queued', 'running'];

/** A step's state decides whether it is open, and what it says if it is not. */
type StepState =
  | { open: true }
  | { open: false; why: string; fix?: { label: string; to: string } };

export function Source() {
  const nav = useNavigate();
  const icps = useRecords<IcpProfile & { id: string }>('icp_profiles');
  const personas = useRecords<Persona & { id: string }>('personas');
  const companies = useRecords<Company>('companies');
  const staged = useRecords<Discovered>('discovered_accounts', { status: 'new' });

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // step 2
  const [want, setWant] = useState('10');
  const [research, setResearch] = useState<AgentJob | null>(null);
  const [promoted, setPromoted] = useState<string[]>([]);

  // step 3
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [enrich, setEnrich] = useState(false);
  const [build, setBuild] = useState<BuildJob | null>(null);

  // step 4
  const [people, setPeople] = useState<ContactSuggestion[]>([]);
  const [revealing, setRevealing] = useState<string | null>(null);

  const timer = useRef<number | null>(null);

  const icp = useMemo(
    () => (icps.rows ?? []).find((i) => i.is_default) ?? (icps.rows ?? [])[0] ?? null,
    [icps.rows],
  );

  const loadPeople = useCallback(() => {
    listSuggestions().then((r) => setPeople(r.suggestions)).catch(() => {});
  }, []);
  useEffect(() => { loadPeople(); }, [loadPeople]);

  // One poller for whichever background job is in flight. Two intervals racing
  // each other is how a finished job keeps saying "running".
  useEffect(() => {
    const live = (research && LIVE.includes(research.status)) || (build && LIVE.includes(build.status));
    if (!live) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setInterval(() => {
      if (research && LIVE.includes(research.status)) {
        getResearchJob(research.id).then((r) => {
          setResearch(r.job);
          // The job's last act is WRITING the staged rows — reloading only the
          // job meant the screen announced "N new candidates staged" over an
          // empty list until a hard refresh. Same pattern the build branch
          // below always had.
          if (!LIVE.includes(r.job.status)) void staged.reload();
        }).catch(() => {});
      }
      if (build && LIVE.includes(build.status)) {
        getBuildJob(build.id).then((r) => {
          setBuild(r.job);
          if (!LIVE.includes(r.job.status)) { loadPeople(); void companies.reload(); }
        }).catch(() => {});
      }
    }, 1500);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [research, build, loadPeople, companies]);

  const say = (e: unknown) => setError(e instanceof ApiError ? e.message : String(e));

  // ── the chain ─────────────────────────────────────────────────────────────
  const hasIcp = Boolean(icp);
  const hasPersonas = (personas.rows ?? []).length > 0;
  const accounts = companies.rows ?? [];
  const workable = accounts.filter((c) => c.url);
  const unenriched = workable.filter((c) => !c.enriched_at);

  const step2: StepState = hasIcp
    ? { open: true }
    : { open: false, why: 'The agent scores what it finds against your ICP. Without one there is nothing to score.', fix: { label: 'Write the ICP', to: '/icp' } };

  const step3: StepState = workable.length > 0
    ? { open: true }
    : { open: false, why: accounts.length === 0
      ? 'No accounts yet. Find some above, or add one you already know.'
      : 'Every account is missing a website, and a domain is what Apollo searches on.',
    fix: { label: 'Add an account', to: '/companies' } };

  const step4: StepState = people.length > 0
    ? { open: true }
    : { open: false, why: 'Nobody staged yet. Step 3 finds them — the search itself is free.' };

  // Selection facts for step 3, priced honestly: already-enriched accounts are
  // skipped by the runner, so charging for them would overstate the cost.
  const pickedRows = workable.filter((c) => picked.has(c.slug));
  const wouldBuy = enrich ? pickedRows.filter((c) => !c.enriched_at).length : 0;

  async function runResearch() {
    setBusy('research'); setError(null);
    try {
      const r = await startResearch({ icp_id: icp?.id, want: Number(want) || 10 });
      const j = await getResearchJob(r.job_id);
      setResearch(j.job);
    } catch (e) { say(e); } finally { setBusy(null); }
  }

  // Sequential on purpose, and the shape is lifted from Research.tsx rather
  // than reinvented: the API is one insert per call, and firing 25 at Neon at
  // once buys nothing but a burst of connection failures on this link. It also
  // marks the staged row 'added' with the new company id — skip that and the
  // candidate reappears in the queue forever.
  async function promoteAll() {
    setBusy('promote'); setError(null);
    const added: string[] = [];
    for (const d of staged.rows ?? []) {
      // One bad candidate must not kill the batch — the old `break` meant a
      // single failure abandoned everything after it, silently.
      try {
        // NO icp_score. It is a DERIVED field, the server 400s any write that
        // carries it, and this loop breaks on the first error — so "Add all N"
        // had never added a single account since the derived-fields gate went
        // in. The score is recomputed on read by fit-core; sending it was
        // never necessary, only fatal.
        const co = await createRecord<{ id: string; slug: string }>('companies', {
          name: d.name, domain: d.domain, url: d.domain ? `https://${d.domain}` : null,
          icp_id: d.icp_id, lifecycle: 'target',
          stage: 'identified', source: `Research — ${d.query ?? d.provider}`,
        });
        added.push(co.record.slug);
        await staged.update(d.id, { status: 'added', company_id: co.record.id });
      } catch (e) { say(e); }
    }
    setPromoted(added);
    // Pre-select what was just promoted — re-picking the same ten on the next
    // step is the whole cost this screen exists to remove.
    setPicked((p) => new Set([...p, ...added]));
    void staged.reload();
    void companies.reload();
    setBusy(null);
  }

  async function runBuild() {
    setBusy('build'); setError(null);
    try {
      const r = await startBuild([...picked], enrich);
      const j = await getBuildJob(r.job_id);
      setBuild(j.job);
    } catch (e) { say(e); } finally { setBusy(null); }
  }

  async function reveal(s: ContactSuggestion) {
    if (!s.apollo_person_id) { setError(`No Apollo id for ${s.name}.`); return; }
    setRevealing(s.id); setError(null);
    try {
      const r = await revealContact(s.company_slug, s.apollo_person_id);
      await markSuggestionAdded(s.id, (r.contact as { id?: string })?.id ?? null);
      loadPeople();
    } catch (e) { say(e); } finally { setRevealing(null); }
  }

  async function drop(s: ContactSuggestion) {
    setRevealing(s.id);
    try { await dismissSuggestion(s.id); loadPeople(); }
    catch (e) { say(e); } finally { setRevealing(null); }
  }

  // Grouped by persona, because that is the decision: which seat am I entering
  // through. Unmatched people are LAST and present — never hidden, because a
  // persona list Kyle wrote last month should not delete a CTO from his view.
  const byPersona = useMemo(() => {
    const m = new Map<string, ContactSuggestion[]>();
    for (const p of people) {
      const k = p.matched_persona ?? '￿Unmatched';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [people]);

  const doneStep = (n: number) =>
    (n === 1 && hasIcp) ||
    (n === 2 && accounts.length > 0) ||
    (n === 3 && people.length > 0) ||
    (n === 4 && people.length === 0 && accounts.length > 0);

  return (
    <>
      <PageHead
        title="Source accounts"
        sub="From your ICP to a committee you can write to. Four steps, and the two that cost money say so first."
      />

      <Err>{error}</Err>

      {/* ── 1 ─────────────────────────────────────────────────────────────── */}
      <Step n={1} done={doneStep(1)} title="Who you are looking for"
        answer={hasIcp
          ? `${icp!.name}${hasPersonas ? ` · ${(personas.rows ?? []).length} personas` : ' · no personas yet'}`
          : 'no ICP yet'}>
        {hasIcp ? (
          <p className="fieldhint">
            Accounts are scored against this, and the people search queries your
            personas' titles.{' '}
            {!hasPersonas && <strong>With no personas the search falls back to a generic exec list.</strong>}{' '}
            <Link to="/icp">Edit</Link>
          </p>
        ) : (
          <p className="fieldhint">
            Nothing below can run without one — there would be nothing to score
            against and no titles to search for. <Link to="/icp">Write your ICP</Link>
          </p>
        )}
      </Step>

      {/* ── 2 ─────────────────────────────────────────────────────────────── */}
      <Step n={2} done={doneStep(2)} title="Find accounts"
        answer={accounts.length > 0 ? `${accounts.length} in the CRM` : 'none yet'}
        blocked={step2.open ? null : step2}>
        <p className="fieldhint">
          The agent searches the web and stages what it finds. It costs Anthropic
          tokens, no Apollo credits, and nothing it proposes becomes an account
          until you add it. <strong>Skip this if you already have the accounts you want.</strong>
        </p>
        <div className="toolbar">
          <label className="field" style={{ maxWidth: 120 }}>
            <span className="field-label">How many</span>
            <input type="number" min={1} max={25} value={want}
              onChange={(e) => setWant(e.target.value)} />
          </label>
          <button disabled={!step2.open || busy !== null || Boolean(research && LIVE.includes(research.status))}
            onClick={() => void runResearch()}>
            {research && LIVE.includes(research.status) ? 'Searching…' : 'Find accounts'}
          </button>
          <span className="cost">Anthropic tokens · no Apollo credits</span>
        </div>

        {research && (
          <p className="fieldhint">
            {research.status === 'failed'
              ? `Failed: ${research.error ?? 'unknown'}`
              : research.progress ?? research.status}
          </p>
        )}

        {(staged.rows ?? []).length > 0 && (
          <>
            <div className="toolbar">
              <button disabled={busy !== null} onClick={() => void promoteAll()}>
                {busy === 'promote' ? 'Adding…' : `Add all ${(staged.rows ?? []).length} to the CRM`}
              </button>
              <span className="cost">free — nothing is bought by adding an account</span>
            </div>
            <ol className="queue">
              {(staged.rows ?? []).slice(0, 12).map((d) => (
                <li className="qitem" key={d.id}>
                  <span className="qicon" aria-hidden="true">⌕</span>
                  <div className="qbody">
                    <div className="qline">
                      <strong>{d.name}</strong>
                      {d.icp_score !== null && <span className="flag">fit {d.icp_score}</span>}
                    </div>
                    <span className="meta">{d.url ?? 'no website found'}</span>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
        {promoted.length > 0 && (
          <p className="fieldhint">
            {promoted.length} added and pre-selected below, so you do not pick them twice.
          </p>
        )}
      </Step>

      {/* ── 3 ─────────────────────────────────────────────────────────────── */}
      <Step n={3} done={doneStep(3)} title="Find the people"
        answer={people.length > 0
          ? `${people.length} staged`
          : `${unenriched.length} of ${workable.length} accounts never enriched`}
        blocked={step3.open ? null : step3}>
        <p className="fieldhint">
          The people search is <strong>free</strong> and returns names, titles and
          seniority — but no email addresses. Firmographics are a separate,
          optional purchase and are what the ICP scores against.
        </p>

        <div className="pickgrid">
          {workable.slice(0, 60).map((c) => (
            <label key={c.slug} className={`pickitem ${picked.has(c.slug) ? 'on' : ''}`}>
              <input type="checkbox" checked={picked.has(c.slug)}
                onChange={() => setPicked((p) => {
                  const n = new Set(p);
                  if (n.has(c.slug)) n.delete(c.slug); else n.add(c.slug);
                  return n;
                })} />
              <span>{c.name}</span>
              {c.enriched_at && <span className="flag flag-ok">enriched</span>}
            </label>
          ))}
        </div>

        <div className="toolbar">
          <label className="check">
            <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} />
            Also buy firmographics <span className="meta">(1 credit per account)</span>
          </label>
          <button className="primary"
            disabled={!step3.open || picked.size === 0 || busy !== null || Boolean(build && LIVE.includes(build.status))}
            onClick={() => void runBuild()}>
            {build && LIVE.includes(build.status)
              ? 'Working…'
              : `Find people at ${picked.size} account${picked.size === 1 ? '' : 's'}`}
          </button>
          <span className="cost">
            {wouldBuy > 0
              ? `up to ${wouldBuy} Apollo credit${wouldBuy === 1 ? '' : 's'} · people search is free`
              : 'free — people search only'}
          </span>
        </div>
        {enrich && pickedRows.length > wouldBuy && (
          <p className="fieldhint">
            {pickedRows.length - wouldBuy} of those are already enriched and are skipped
            rather than re-bought.
          </p>
        )}
        {build && (
          <p className="fieldhint">
            {build.status === 'failed'
              ? `Failed: ${build.error ?? 'unknown'}`
              : build.progress ?? build.status}
          </p>
        )}
      </Step>

      {/* ── 4 ─────────────────────────────────────────────────────────────── */}
      <Step n={4} done={false} title="Pick the committee"
        answer={people.length > 0 ? `${people.length} waiting on you` : 'nothing staged'}
        blocked={step4.open ? null : step4}>
        <p className="fieldhint">
          Grouped by the persona each title matches. Revealing an address is
          <strong> 1 Apollo credit for one person</strong>, and Apollo charges whether
          or not it turns out to hold one. Everyone unmatched is still listed —
          a persona you wrote last month should not hide somebody worth writing to.
        </p>

        {people.length === 0 ? (
          <Empty title="Nobody staged" hint="Run step 3. The search costs nothing." />
        ) : byPersona.map(([key, rows]) => (
          <section className="panel" key={key}>
            <h4>
              {key.startsWith('￿') ? 'Unmatched — your call' : key}
              <span className="count">{rows.length}</span>
            </h4>
            {rows.slice(0, 25).map((s) => (
              <div className="row" key={s.id}>
                <span className="rname">{s.name}</span>
                <span className="meta">{s.title ?? '—'}</span>
                <span className="meta">{s.company_name}</span>
                {s.seniority && <span className="flag">{s.seniority.replace('_', ' ')}</span>}
                <span className="flags">
                  <button className="mini" disabled={revealing !== null}
                    onClick={() => void reveal(s)}>
                    {revealing === s.id ? 'Revealing…' : 'Reveal · 1 credit'}
                  </button>
                  <button className="mini" disabled={revealing !== null}
                    onClick={() => void drop(s)}>Dismiss</button>
                </span>
              </div>
            ))}
            {rows.length > 25 && (
              <p className="fieldhint">
                {rows.length - 25} more — <Link to="/build">see them all</Link>
              </p>
            )}
          </section>
        ))}
      </Step>

      <section className="panel">
        <h4>When the committee is built</h4>
        <p className="fieldhint">
          Open an account and write its cadence, then arm it from there. Sending
          is governed on <Link to="/schedule">Schedule</Link>.
        </p>
        <div className="actions">
          <button onClick={() => nav('/companies')}>Go to accounts</button>
        </div>
      </section>
    </>
  );
}

/**
 * One step.
 *
 * The header carries its own answer — "3 · Find the people · 26 of 27 accounts
 * never enriched" — rather than labelling a container, which is the rule the
 * rest of this app is built on. A blocked step says WHY and links to the fix,
 * because a disabled control with no explanation reads as a broken app.
 */
function Step({ n, title, answer, done, blocked, children }: {
  n: number; title: string; answer: string; done: boolean;
  blocked?: StepState & { open: false } | null;
  children: React.ReactNode;
}) {
  return (
    <section className={`panel wizstep ${done ? 'done' : ''} ${blocked ? 'blocked' : ''}`}>
      <h4>
        <span className="wiznum" aria-hidden="true">{done ? '✓' : n}</span>
        {title}
        <span className="count">{answer}</span>
      </h4>
      {blocked ? (
        <p className="fieldhint">
          {blocked.why}{' '}
          {blocked.fix && <Link to={blocked.fix.to}>{blocked.fix.label}</Link>}
        </p>
      ) : children}
    </section>
  );
}
